import { Controller } from "@hotwired/stimulus"
import { planningMutation } from "../planning_mutation"

// "Woche kopieren": the confirmation dialog for copying a Baustelle's planning of one week onto
// the next. Rendered once on the board; every site header's copy icon
// ([data-planning-week-copy-button]) opens it with that site and the displayed week preset.
export default class extends Controller {
  static targets = ["root", "siteName", "source", "sourceLabel", "targetLabel", "submit"]
  static values = { url: String, weekLabel: String }

  connect() {
    this.siteId = null
    this.boundClick = this.onDocumentClick.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
    document.addEventListener("click", this.boundClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClick)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  // Delegated, because the header icons are re-rendered by Turbo streams after every mutation.
  onDocumentClick(event) {
    const button = event.target.closest("[data-planning-week-copy-button]")
    if (!button) return
    event.preventDefault()
    this.open(button.dataset)
  }

  open({ siteId, siteName, weekStart }) {
    this.siteId = siteId
    this.siteNameTarget.textContent = siteName || ""
    this.sourceTarget.value = weekStart
    this.sourceTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.refreshLabel()
    this.submitTarget.disabled = false
    this.rootTarget.classList.remove("hidden")
    this.rootTarget.classList.add("flex")
    document.addEventListener("keydown", this.boundKeydown)
  }

  close() {
    this.rootTarget.classList.add("hidden")
    this.rootTarget.classList.remove("flex")
    document.removeEventListener("keydown", this.boundKeydown)
  }

  backdrop(event) {
    if (event.target === this.rootTarget) this.close()
  }

  onKeydown(event) {
    if (event.key !== "Escape") return
    if (event.defaultPrevented || this.rootTarget.querySelector(":popover-open")) return
    this.close()
  }

  // Any picked day stands for its whole ISO week (Monday to Sunday); the target is the next one.
  refreshLabel() {
    const monday = this.mondayOf(this.sourceTarget.value)
    if (!monday) {
      this.sourceLabelTarget.textContent = "—"
      this.targetLabelTarget.textContent = "—"
      return
    }
    this.sourceLabelTarget.textContent = this.weekText(monday)
    this.targetLabelTarget.textContent = this.weekText(this.addDays(monday, 7))
  }

  async submit() {
    const monday = this.mondayOf(this.sourceTarget.value)
    if (!this.siteId || !monday) return
    this.submitTarget.disabled = true
    const { ok } = await planningMutation({
      url: this.urlValue,
      body: { site_id: Number(this.siteId), week_start: this.isoDate(monday) },
      component: "planning-week-copy",
    })
    if (ok) this.close()
    else this.submitTarget.disabled = false
  }

  // Local-time date helpers: toISOString() would shift the day across the UTC boundary.
  mondayOf(iso) {
    if (!iso) return null
    const [year, month, day] = iso.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) return null
    return this.addDays(date, -((date.getDay() + 6) % 7))
  }

  addDays(date, days) {
    const copy = new Date(date)
    copy.setDate(copy.getDate() + days)
    return copy
  }

  isoDate(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  shortDate(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.`
  }

  // ISO 8601 week number: the week holding the year's first Thursday is week 1.
  isoWeek(date) {
    const thursday = this.addDays(date, 3 - ((date.getDay() + 6) % 7))
    const firstThursday = new Date(thursday.getFullYear(), 0, 4)
    return (
      1 +
      Math.round(
        (thursday - this.addDays(firstThursday, -((firstThursday.getDay() + 6) % 7) + 3)) /
          604800000,
      )
    )
  }

  weekText(monday) {
    const sunday = this.addDays(monday, 6)
    return this.weekLabelValue
      .replace("%{week}", this.isoWeek(monday))
      .replace("%{from}", this.shortDate(monday))
      .replace("%{to}", `${this.shortDate(sunday)}${sunday.getFullYear()}`)
  }
}
